import { ValidDate } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import { emptyToUndefined } from "@/utils/format.util";
import { joinDisplayName } from "@/utils/name.util";
import { tenYearsEndMonth } from "../../date-time-picker/DateTimePicker";
import { FormField } from "../../form-field/FormField";
import type { FamilyMember, LinkableMember, Profile } from "../data/profile";
import { useMemberProfile } from "../MemberProfileContext";
import { OwnedField } from "./FormFields";
import { FormShell } from "./ProfileOverlayShell";

export function FamilyMemberForm({
    kind,
    member,
    onClose,
}: {
    kind: "limited" | "linked";
    member?: FamilyMember;
    onClose: () => void;
}) {
    switch (kind) {
        case "linked":
            return <LinkedFamilyForm member={member} onClose={onClose} />;
        case "limited":
            return <LimitedFamilyForm member={member} onClose={onClose} />;
        default: {
            const _exhaustive: never = kind;
            return _exhaustive;
        }
    }
}

const limitedFamilySchema = z.object({
    firstName: z.string().trim().min(1, "First name is required"),
    lastName: z.string().trim().min(1, "Last name is required"),
    relationship: z.string().trim().min(3, "Enter at least 3 characters"),
    mustKnow: z.string(),
    dateOfBirth: ValidDate.optional(),
    nationality: z.string(),
    passportName: z.string(),
    passportNumber: z.string(),
    passportExpiryDate: ValidDate.optional(),
    frequentFlyerNumber: z.string(),
});

type LimitedValues = z.infer<typeof limitedFamilySchema>;

const linkedFamilySchema = z.object({
    memberId: z.string().min(1, "Member is required"),
});

function LimitedFamilyForm({
    member,
    onClose,
}: {
    member?: FamilyMember;
    onClose: () => void;
}) {
    const { createLimitedFamily, editLimitedFamily } = useMemberProfile();
    const { control, handleSubmit } = useForm<LimitedValues>({
        resolver: zodResolver(limitedFamilySchema),
        defaultValues: {
            firstName: member?.firstName ?? "",
            lastName: member?.lastName ?? "",
            relationship: member?.detail ?? "",
            mustKnow: member?.mustKnow ?? "",
            dateOfBirth: member?.dateOfBirth ? parseISO(member.dateOfBirth) : undefined,
            nationality: member?.nationality ?? "",
            passportName: member?.passportName ?? "",
            passportNumber: member?.passportNumber ?? "",
            passportExpiryDate: member?.passportExpiryDate
                ? parseISO(member.passportExpiryDate)
                : undefined,
            frequentFlyerNumber: member?.frequentFlyerNumber ?? "",
        },
    });

    function onSubmit(values: LimitedValues) {
        const payload = {
            firstName: values.firstName,
            lastName: values.lastName,
            relationship: values.relationship,
            mustKnow: emptyToUndefined(values.mustKnow),
            dateOfBirth: values.dateOfBirth
                ? format(values.dateOfBirth, "yyyy-MM-dd")
                : undefined,
            nationality: emptyToUndefined(values.nationality),
            passportName: emptyToUndefined(values.passportName),
            passportNumber: emptyToUndefined(values.passportNumber),
            passportExpiryDate: values.passportExpiryDate
                ? format(values.passportExpiryDate, "yyyy-MM-dd")
                : undefined,
            frequentFlyerNumber: emptyToUndefined(values.frequentFlyerNumber),
        };

        if (member) {
            editLimitedFamily(member.id, payload, onClose);
        } else {
            createLimitedFamily(payload, onClose);
        }
    }

    return (
        <FormShell
            title={member ? "Edit family member" : "Add limited profile"}
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <OwnedField
                id="fam-first"
                label="First name"
                kind="text"
                control={control}
                name="firstName"
                required
            />
            <OwnedField
                id="fam-last"
                label="Last name"
                kind="text"
                control={control}
                name="lastName"
                required
            />
            <OwnedField
                id="fam-rel"
                label="Relationship"
                kind="text"
                control={control}
                name="relationship"
                required
                rules={{
                    minLength: { value: 3, message: "Enter at least 3 characters" },
                }}
            />
            <OwnedField
                id="fam-must"
                label="Must know"
                kind="textarea"
                control={control}
                name="mustKnow"
            />
            <OwnedField
                id="fam-dob"
                label="Date of birth"
                kind="date"
                control={control}
                name="dateOfBirth"
            />
            <OwnedField
                id="fam-nat"
                label="Nationality"
                kind="text"
                control={control}
                name="nationality"
            />
            <OwnedField
                id="fam-pname"
                label="Passport name"
                kind="text"
                control={control}
                name="passportName"
            />
            <OwnedField
                id="fam-pnum"
                label="Passport number"
                kind="text"
                control={control}
                name="passportNumber"
            />
            <OwnedField
                id="fam-pexp"
                label="Passport expires"
                kind="date"
                control={control}
                name="passportExpiryDate"
                endMonth={tenYearsEndMonth()}
            />
            <OwnedField
                id="fam-ff"
                label="Frequent flyer"
                kind="text"
                control={control}
                name="frequentFlyerNumber"
            />
        </FormShell>
    );
}

function LinkedFamilyForm({
    member,
    onClose,
}: {
    member?: FamilyMember;
    onClose: () => void;
}) {
    const { profile, members, createLinkedFamily, replaceLinkedFamily } =
        useMemberProfile();
    const {
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(linkedFamilySchema),
        defaultValues: { memberId: member?.linkedMemberId ?? "" },
    });
    const memberId = watch("memberId");
    const options = linkableMembers(members, profile, member);
    const selected = options.find((option) => option.id === memberId) ?? null;

    function onSubmit(values: { memberId: string }) {
        const selectedMember = options.find((option) => option.id === values.memberId);
        if (!selectedMember) return;

        if (member?.linkedMemberId && member.linkedMemberId !== selectedMember.id) {
            replaceLinkedFamily(
                member.linkedMemberId,
                { memberIdToLink: selectedMember.id },
                onClose,
            );
        } else if (!member) {
            createLinkedFamily({ memberIdToLink: selectedMember.id }, onClose);
        } else {
            onClose();
        }
    }

    return (
        <FormShell
            title="Link an existing member"
            subtitle={`Linked to ${profile.memberName}`}
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <FormField
                label="Member"
                htmlFor="fam-member"
                error={errors.memberId?.message}
            >
                <Combobox
                    autoHighlight
                    items={options}
                    value={selected}
                    onValueChange={(next) => {
                        setValue("memberId", next?.id ?? "", { shouldValidate: true });
                    }}
                    itemToStringLabel={(member) =>
                        joinDisplayName(member.firstName, member.lastName)
                    }
                    isItemEqualToValue={(a, b) => a.id === b.id}
                    filter={memberMatchesQuery}
                >
                    <ComboboxInput
                        id="fam-member"
                        placeholder="Search members"
                        showTrigger
                        className="w-full"
                        aria-invalid={Boolean(errors.memberId) || undefined}
                    />
                    <ComboboxContent>
                        <ComboboxEmpty>No members match</ComboboxEmpty>
                        <ComboboxList>
                            {(option: LinkableMember) => (
                                <ComboboxItem key={option.id} value={option}>
                                    {joinDisplayName(option.firstName, option.lastName)}
                                </ComboboxItem>
                            )}
                        </ComboboxList>
                    </ComboboxContent>
                </Combobox>
            </FormField>
        </FormShell>
    );
}

function linkableMembers(
    members: LinkableMember[],
    profile: Profile,
    current?: FamilyMember,
) {
    const linkedIds = new Set(
        profile.family
            .filter(
                (item) =>
                    item.kind === "linked" &&
                    item.linkedMemberId !== current?.linkedMemberId,
            )
            .map((item) => item.linkedMemberId)
            .filter(Boolean),
    );

    return members.filter(
        (option) => option.id !== profile.memberId && !linkedIds.has(option.id),
    );
}

function memberMatchesQuery(member: LinkableMember, query: string) {
    const term = query.trim().toLowerCase();
    if (!term) return true;

    return (
        joinDisplayName(member.firstName, member.lastName)
            .toLowerCase()
            .includes(term) ||
        member.firstName.toLowerCase().includes(term) ||
        member.lastName.toLowerCase().includes(term)
    );
}
